import React, { createContext, useState, useContext, useEffect } from 'react';
import { collection, doc, setDoc, getDoc, deleteDoc, updateDoc, increment } from 'firebase/firestore';
import { FirebaseContext } from './Firebase';
import { UserContext } from './User';
import api from '../utils/api';
import logger from '../utils/logger';

export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { db } = useContext(FirebaseContext);
  const { user, saveUserIfNotExist, updateTotalCostAndTokens } = useContext(UserContext);
  const [sessionTitle, setSessionTitle] = useState('New Chat Session');
  const [session_title_summary, setSessionTitleSummary] = useState('New Chat Session');
  const [isSessionTitleManuallyUpdated, setIsSessionTitleManuallyUpdated] = useState(false);
  const [contextWindow, setContextWindow] = useState([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [chatId, setChatId] = useState(null);
  const [apiContext, setApiContext] = useState([]);
  const [highestInteractionId, setHighestInteractionId] = useState(0);
  const [selectedModel, setSelectedModel] = useState('openai');
  const [summarizeTokenThreshold, setSummarizeTokenThreshold] = useState(60000);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [modelMetadata, setModelMetadata] = useState({}); 

  useEffect(() => {
    if (!chatId && user) {
      const newChatRef = doc(collection(doc(db, 'users', user.uid), 'chats'));
      setChatId(newChatRef.id);
      setHighestInteractionId(0); // Initialize highest interaction ID for new chat
    }
  }, [db, chatId, user]);

  // Fetch model metadata on mount
  useEffect(() => {
    const fetchModelMetadata = async () => {
      try {
        const response = await api.get('/models/metadata');
        setModelMetadata(response.data.models || {});
        logger.log('Model metadata loaded:', response.data.models);
      } catch (error) {
        logger.error('Failed to fetch model metadata:', error);
      }
    };
    fetchModelMetadata();
  }, []);

  const calculateTotalTokens = () => {
    return contextWindow.reduce((acc, curr) => {
      if (curr.inputTokens && curr.outputTokens) {
        return acc + curr.inputTokens + curr.outputTokens;
      }
      return acc;
    }, 0);
  };

  // Calculate cost for a single interaction
  const calculateInteractionCost = (inputTokens, outputTokens, modelId) => {
    const modelData = modelMetadata[modelId];
    if (!modelData || !modelData.pricing) {
      return { inputCost: 0, outputCost: 0, totalCost: 0 };
    }

    const { input_cost_per_million, output_cost_per_million } = modelData.pricing;
    const inputCost = (inputTokens / 1_000_000) * input_cost_per_million;
    const outputCost = (outputTokens / 1_000_000) * output_cost_per_million;

    return {
      inputCost: parseFloat(inputCost.toFixed(6)),
      outputCost: parseFloat(outputCost.toFixed(6)),
      totalCost: parseFloat((inputCost + outputCost).toFixed(6))
    };
  };

  // Calculate total cost for all interactions
  const calculateTotalCost = () => {
    return contextWindow.reduce((acc, curr) => {
      if (curr.inputCost && curr.outputCost) {
        return acc + curr.inputCost + curr.outputCost;
      }
      return acc;
    }, 0);
  };

  const saveChatSession = async () => {
    if (!user || !chatId) return;

    await saveUserIfNotExist();

    const userDocRef = doc(db, 'users', user.uid);
    const chatDocRef = doc(userDocRef, 'chats', chatId);

    const totalTokens = calculateTotalTokens();
    const totalCost = calculateTotalCost();

    const chatDoc = await getDoc(chatDocRef);
    const dataToUpdate = {
      sessionTitle,
      totalTokens,
      totalCost
    };

    if (isSessionTitleManuallyUpdated) {
      logger.log("if this was manually updated, vs auto, then set extra flag");
      dataToUpdate.isManualTitle = true;
    }

    if (!chatDoc.exists()) {
      await setDoc(chatDocRef, {
        ...dataToUpdate,
        timestamp: new Date()
      });
    } else {
      await updateDoc(chatDocRef, dataToUpdate);
    }
  };  

  const addInteraction = async (interaction) => {
    if (!user || !chatId) return;

    await saveUserIfNotExist();

    const userDocRef = doc(db, 'users', user.uid);
    const chatDocRef = doc(userDocRef, 'chats', chatId);

    const chatDoc = await getDoc(chatDocRef);
    if (!chatDoc.exists()) {
      await setDoc(chatDocRef, {
        sessionTitle,
        timestamp: new Date(),
        totalTokens: calculateTotalTokens()
      });
    }

    const newInteractionId = (highestInteractionId + 1).toString();
    setHighestInteractionId(highestInteractionId + 1); // Increment the highest interaction ID

    const interactionsCollectionRef = collection(chatDocRef, 'interactions');
    const interactionDocRef = doc(interactionsCollectionRef, newInteractionId);

    const inputTokens = interaction.inputTokens;
    const outputTokens = interaction.outputTokens;
    const modelUsed = interaction.model;

    // Calculate cost for this interaction
    const { inputCost, outputCost, totalCost: interactionCost } = calculateInteractionCost(
      inputTokens,
      outputTokens,
      modelUsed
    );

    // Save interaction with cost
    await setDoc(interactionDocRef, {
      ...interaction,
      id: newInteractionId,
      inputCost,
      outputCost
    });

    // Update the chat document (tokens and cost)
    await updateDoc(chatDocRef, {
      totalTokens: increment(inputTokens + outputTokens),
      totalCost: increment(interactionCost)
    });

    // Update the top-level user document (tokens and cost)
    await updateDoc(userDocRef, {
      totalTokens: increment(inputTokens + outputTokens),
      totalCost: increment(interactionCost)
    });

    // Update the local state with the new interaction ID and cost
    setContextWindow(prev => prev.map(item => {
      if (item.temp === "butthead") {
        const newItem = { ...item, id: newInteractionId, inputCost, outputCost };
        delete newItem.temp; // Remove the temp property
        return newItem;
      }
      return item;
    }));

    setApiContext(prev => prev.map(item => {
      if (item.temp === "butthead") {
        const newItem = { ...item, id: newInteractionId };
        delete newItem.temp; // Remove the temp property
        return newItem;
      }
      return item;
    }));

    setTotalTokens(prevTokens => prevTokens + inputTokens + outputTokens);
    setTotalCost(prevCost => prevCost + interactionCost);

    // Update UserContext state (cost and tokens)
    updateTotalCostAndTokens(interactionCost, inputTokens + outputTokens);
  };

  const deleteInteraction = async (interactionId) => {
    if (!user || !chatId) return;

    const userDocRef = doc(db, 'users', user.uid);
    const chatDocRef = doc(userDocRef, 'chats', chatId);
    const interactionsCollectionRef = collection(chatDocRef, 'interactions');
    const interactionDocRef = doc(interactionsCollectionRef, interactionId);

    await deleteDoc(interactionDocRef);

    // Update the local state
    setContextWindow(prev => prev.filter(item => item.id !== interactionId));
    setApiContext(prev => prev.filter(item => item.id !== interactionId));
  };

  const transformInteractions = (interactions, isManualTitle) => {
    // Sort interactions based on the id
    const sortedInteractions = interactions.sort((a, b) => parseInt(a.id) - parseInt(b.id));
  
    const highestId = Math.max(...sortedInteractions.map(interaction => parseInt(interaction.id)), 0);
    setHighestInteractionId(highestId);
  
    const apiContext = sortedInteractions.reduce((acc, interaction) => {
      acc.push(
        { role: 'user', content: interaction.user_content, id: interaction.id },
        { role: 'assistant', content: interaction.assistant_content, id: interaction.id }
      );
      return acc;
    }, []);
  
    const contextWindow = sortedInteractions.reduce((acc, interaction) => {
      const userContent = Array.isArray(interaction.user_content) ? interaction.user_content : [{ type: 'text', text: interaction.user_content }];
      acc.push({ role: 'user', content: userContent, id: interaction.id });
  
      const assistantContent = Array.isArray(interaction.assistant_content) ? interaction.assistant_content : [{ type: 'text', text: interaction.assistant_content }];
      acc.push({
        role: 'assistant',
        content: assistantContent,
        inputTokens: interaction.inputTokens,
        outputTokens: interaction.outputTokens,
        model: interaction.model,
        id: interaction.id
      });
      return acc;
    }, []);
  
    setApiContext(apiContext);
    setContextWindow(contextWindow);

    // Set session title manually updated flag if isManualTitle is true
    if (isManualTitle) {
      setIsSessionTitleManuallyUpdated(true);
    }

    // Check if summarization is needed
    let totalTokens = sortedInteractions.reduce((acc, interaction) => {
      return acc + (interaction.inputTokens || 0) + (interaction.outputTokens || 0);
    }, 0);
    checkAndSummarizeApiContext(totalTokens, apiContext, contextWindow);
  
    logger.log('transformInteractions(interactions)', interactions);
    logger.log('Transformed apiContext:', apiContext);
    logger.log('Transformed contextWindow:', contextWindow);
  };

  const checkAndSummarizeApiContext = async (totalTokens, apiContext, contextWindow) => {
    // Get the current model's metadata to find the correct threshold
    const currentModelKey = Object.keys(modelMetadata).find(key =>
      modelMetadata[key].provider === selectedModel
    );

    const threshold = currentModelKey
      ? modelMetadata[currentModelKey].summarization_threshold
      : summarizeTokenThreshold; // fallback to default

    logger.log("Context check:", {
      totalTokens,
      threshold,
      shouldSummarize: totalTokens >= threshold,
      model: selectedModel
    });

    if (totalTokens >= threshold) {
      logger.log("🔄 Triggering auto-summarization at 80% of context window");
      await summarizeAndTruncateApiContext(apiContext, contextWindow);
    }
  };

  const summarizeAndTruncateApiContext = async (apiContext, contextWindow) => {
    try {
      setIsSummarizing(true);

      // Summarize the earliest 50% of the conversation
      const sliceIndex = Math.floor(apiContext.length * 0.5);

      logger.log(`Summarizing earliest 50% of conversation (${sliceIndex} of ${apiContext.length} messages)`);

      let summarizeSlice = apiContext.slice(0, sliceIndex);
      logger.log("Summarize slice:", summarizeSlice);
  
      // Prepare the prompt for the AI
      const prompt = 'Please provide a concise summary of the chatML conversation and existing system Context if any:';
  
      // Create the request payload for the summarization API using ai_backend
      const summaryRequest = {
        provider: selectedModel, // Use the selected model if needed
        prompt: [...summarizeSlice, { role: 'system', content: prompt }],
        task: null,
        metadata: {}
      };

      // Call the summarization API using ai_backend
      const response = await api.post('/callAI', summaryRequest, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Log the summary from the API response
      const summary = response.data.text || response.data.result?.text;
      logger.log("Summary from the API:", summary);
  
      // Update the apiContext with the new truncated context and summary
      const remainingContext = apiContext.slice(sliceIndex);
      const newSystemMessage = { role: 'system', content: [{type:"text", text:summary}] };
      remainingContext.unshift(newSystemMessage);
  
      // Update the apiContext with the new truncated context
      setApiContext(remainingContext);
      logger.log('apiContext truncated and summarized', remainingContext);
    } catch (error) {
      logger.error('Error summarizing and truncating apiContext:', error);
    } finally {
      setIsSummarizing(false); // Set isSummarizing to false
    }
  };
  
  return (
    <ChatContext.Provider value={{
      sessionTitle,
      setSessionTitle,
      isSessionTitleManuallyUpdated,
      setIsSessionTitleManuallyUpdated,
      session_title_summary,
      setSessionTitleSummary,
      contextWindow,
      setContextWindow,
      totalTokens,
      setTotalTokens,
      totalCost,
      setTotalCost,
      addInteraction,
      chatId,
      setChatId,
      saveChatSession,
      apiContext,
      setApiContext,
      setHighestInteractionId,
      deleteInteraction,
      transformInteractions,
      checkAndSummarizeApiContext,
      modelMetadata,
      isSummarizing
    }}>
      {children}
    </ChatContext.Provider>
  );
};
